// Buscador rápido del topbar del Portal Vendedor (mock `portal-vendedor.html` §topbar): encuentra al
// cliente desde cualquier pantalla y abre su ficha en Clientes.
//
// Busca por teléfono o CURP COMPLETOS: para el vendedor `/customers/search` solo resuelve esos dos
// y con otro término devuelve `[]`, así que la llamada se ahorra y la pantalla lo dice.
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { Input } from '@/kit';
import { useAuth } from '@/hooks/useAuth';
import type { Permission } from '@/api/types/permissions';
import { customerFullName } from '@/api/services/retail-customer';
import { useExactCustomerSearch } from '../portal/customers/api/useSellerCustomers';
import { isExactSearchTerm } from '../portal/customers/lib/customerSearchTerm';

const HINT = 'Escribe el teléfono completo (10 dígitos) o la CURP.';
const PANEL =
    'absolute right-0 z-50 mt-1 max-h-[340px] w-[340px] overflow-y-auto rounded-md border border-border bg-card shadow-lg';
const MESSAGE = 'px-3 py-2.5 text-[11.5px] text-muted-foreground';

export function CustomerQuickSearch() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [term, setTerm] = useState('');
    const [open, setOpen] = useState(false);

    const trimmed = term.trim();
    const search = useExactCustomerSearch(isExactSearchTerm(trimmed) ? trimmed : null);
    const results = search.data ?? [];

    if (!hasPermission('retail.customer.search' as Permission)) return null;

    // Clientes abre la ficha por id aunque no esté entre lo cargado.
    const openCustomer = (retailCustomerId: string): void => {
        setOpen(false);
        setTerm('');
        void navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: { customer: retailCustomerId },
        });
    };

    return (
        <div className="relative">
            <Search
                className="pointer-events-none absolute left-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                aria-hidden
            />
            <Input
                type="search"
                value={term}
                aria-label="Buscar cliente"
                placeholder="Buscar cliente por teléfono o CURP"
                className="h-7 w-[240px] pl-6 text-[11.5px]"
                onFocus={() => {
                    setOpen(true);
                }}
                onChange={(event) => {
                    setTerm(event.target.value);
                    setOpen(true);
                }}
            />
            {open && trimmed !== '' && (
                <div className={PANEL}>
                    {!isExactSearchTerm(trimmed) && <p className={MESSAGE}>{HINT}</p>}
                    {isExactSearchTerm(trimmed) && search.isPending && (
                        <p className={MESSAGE}>Buscando…</p>
                    )}
                    {/* La búsqueda caída NO se lee como «no existe»: el vendedor daría de alta al
                        mismo cliente otra vez. */}
                    {isExactSearchTerm(trimmed) && search.isError && (
                        <div role="alert" className={MESSAGE}>
                            <p>No se pudo buscar.</p>
                            <button
                                type="button"
                                className="mt-1 text-primary hover:underline"
                                onClick={() => void search.refetch()}
                            >
                                Reintentar
                            </button>
                        </div>
                    )}
                    {isExactSearchTerm(trimmed) &&
                        !search.isPending &&
                        !search.isError &&
                        results.length === 0 && <p className={MESSAGE}>Sin coincidencias.</p>}
                    {results.length > 0 && (
                        <div role="listbox" aria-label="Resultados de la búsqueda">
                            {results.map((result) => (
                                <button
                                    key={result.retailCustomerId}
                                    type="button"
                                    role="option"
                                    aria-selected={false}
                                    onClick={() => {
                                        openCustomer(result.retailCustomerId);
                                    }}
                                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-accent"
                                >
                                    <span className="text-[12px] font-medium">
                                        {customerFullName(result)}
                                    </span>
                                    <span className="text-[10.5px] text-muted-foreground">
                                        {result.phone}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
